import React, { useState } from "react";
import { Button } from "../components/Button.jsx";
import { WaitingForOthers } from "../components/WaitingForOthers.jsx";
import { usePlayer, usePlayers, useStage, useRound, useGame } from "@empirica/core/player/classic/react";
import { savePlayerMeta } from "../utils/savePlayerMeta.js";
import { flushBehaviorTracking } from "../utils/integrityTracking.js";
import { getTaskConfig } from "../utils/taskConfig.js";
import { getNicknameFromId, getAnimalFromId, getAnimalEmoji, getAnimalFromPlayer, getNicknameFromPlayer } from "../components/Avatar.jsx";
import {
  getCompensationConfig,
  getReflectionTiming,
  isBaseRateStaticSyntheticGroupCondition,
  usesPostPeerChangedAnswerConfidence,
  usesPostPeerClosedChoice,
  usesPostPeerNoRerate,
} from "../utils/conditionUtils.js";
import { NO_SPINNER_CLASS, typedNumberInputProps } from "../utils/numberInput.js";

const CONVINCINGNESS_SCALE = [
  { value: 1, label: "Not at all" },
  { value: 2, label: "Slightly" },
  { value: 3, label: "Moderately" },
  { value: 4, label: "Very" },
  { value: 5, label: "Extremely" },
];

export function FinalVote() {
  const player = usePlayer();
  const players = usePlayers();
  const stage = useStage();
  const round = useRound();
  const game = useGame();
  // Collapsed on arrival in the static-synthetic group conditions: the complete
  // task description was presented on the first prediction page, and every
  // screen after it hides the description behind this toggle. The live
  // discussion conditions keep the description open, as before.
  const [showLoanCase, setShowLoanCase] = useState(
    () => !isBaseRateStaticSyntheticGroupCondition(game?.get("treatment") || {})
  );
  const [showPeerRationales, setShowPeerRationales] = useState(false);
  const [mostConvincingPlayerId, setMostConvincingPlayerId] = useState(() => {
    return player.stage.get("mostConvincingPlayerId") || null;
  });
  const [mostHelpfulEarlierParticipantResponse, setMostHelpfulEarlierParticipantResponse] = useState(() => {
    return player.stage.get("mostHelpfulEarlierParticipantResponse") || null;
  });
  // Detect treatment type
  const treatment = game.get("treatment") || {};
  const taskConfig = getTaskConfig(treatment);
  const compensation = getCompensationConfig(treatment);
  const isBaseRateStaticSyntheticGroup =
    isBaseRateStaticSyntheticGroupCondition(treatment);
  // Post-peer conditions collect a final confidence and a second convincingness
  // rating here. Both are gated so the existing conditions render as before.
  const isPostPeerReflection =
    getReflectionTiming(treatment) === "post-peer" && isBaseRateStaticSyntheticGroup;
  const [finalConfidence, setFinalConfidence] = useState(() => {
    const existing = player.get("finalConfidence");
    return existing === undefined || existing === null ? "" : String(existing);
  });
  const [previousAnswerConfidence, setPreviousAnswerConfidence] = useState(() => {
    const existing = player.get("finalConfidenceForPreviousAnswer");
    return existing === undefined || existing === null ? "" : String(existing);
  });
  const [peerConvincingnessRatingsPost, setPeerConvincingnessRatingsPost] = useState(() => {
    const stored =
      player.stage.get("peerConvincingnessRatingsPost") ||
      player.get("peerConvincingnessRatingsPost") ||
      {};
    return typeof stored === "object" && stored !== null ? stored : {};
  });
  const staticSyntheticPeersRaw = isBaseRateStaticSyntheticGroup
    ? game.get("staticSyntheticPeers") || round.get("staticSyntheticPeers") || []
    : [];
  const staticSyntheticPeers = Array.isArray(staticSyntheticPeersRaw)
    ? staticSyntheticPeersRaw
    : [];
  const options = taskConfig.options || [];
  const predictionInput = taskConfig.predictionInput || {};
  const isNumericPrediction = predictionInput.type === "number";
  const normalizePredictionChoice = (value) => {
    if (typeof taskConfig.normalizePredictionValue === "function") {
      return taskConfig.normalizePredictionValue(value);
    }
    return String(value ?? "").trim();
  };
  const formatBelief = (value) => {
    if (typeof taskConfig.formatPredictionDisplay === "function") {
      return taskConfig.formatPredictionDisplay(value);
    }
    return String(value ?? "").trim();
  };
  const [predictionDraft, setPredictionDraft] = useState(() => {
    return player.round.get("finalBelief") || "";
  });
  const [predictionError, setPredictionError] = useState("");
  const recordedFinalBelief = normalizePredictionChoice(player.round.get("finalBelief"));
  const hasVoted = recordedFinalBelief !== "";
  const normalizedDraft = normalizePredictionChoice(predictionDraft);
  const draftMatchesRecorded = !isNumericPrediction || normalizedDraft === recordedFinalBelief;

  // askall conditions: the final answer is restricted to the participant's own
  // initial estimate or one of the two synthetic peers' answers. Choices with
  // the same value merge into one option, so the set can hold three, two, or
  // one entries. The rule is announced on the initial prediction page. Option
  // order is own answer first, then the peers in display order; a merged
  // option keeps the earliest of its holders' positions.
  //
  // The ask-all follow-up (postPeerAskAllOpenEntry) takes a typed final answer,
  // so the closed choice below applies only to the piloted ask-all arms.
  const postPeerClosedChoice = usesPostPeerClosedChoice(treatment);
  const askAllOwnAnswer = postPeerClosedChoice
    ? player.get("postPeerAskAllOwnAnswer") ??
      player.get("preLobbyPredictionBeforeAI") ??
      player.get("preLobbyPrediction") ??
      (game.get("playerBeliefs") || {})[player.id]?.initialBelief
    : undefined;
  const askAllOptions = (() => {
    if (!postPeerClosedChoice) {
      return [];
    }
    const holdersByValue = new Map();
    const addHolder = (rawValue, holder) => {
      const normalized = normalizePredictionChoice(rawValue);
      if (!normalized) {
        return;
      }
      if (!holdersByValue.has(normalized)) {
        holdersByValue.set(normalized, []);
      }
      holdersByValue.get(normalized).push(holder);
    };
    addHolder(askAllOwnAnswer, { kind: "own" });
    const orderedAskAllPeers = [...staticSyntheticPeers].sort(
      (a, b) => (Number(a?.displayPosition) || 0) - (Number(b?.displayPosition) || 0)
    );
    orderedAskAllPeers.forEach((peer) => {
      addHolder(peer?.initialBelief, {
        kind: "peer",
        id: String(peer?.id || ""),
        name: peer?.name || getNicknameFromId(peer?.id),
      });
    });
    const builtOptions = [];
    holdersByValue.forEach((holders, value) => {
      const peerNames = holders
        .filter((holder) => holder.kind === "peer")
        .map((holder) => holder.name);
      const includesOwn = holders.some((holder) => holder.kind === "own");
      let label;
      if (includesOwn && peerNames.length === 0) {
        label = "Keep your answer";
      } else if (includesOwn) {
        label = `Keep your answer (same as ${peerNames.join(" and ")}'s)`;
      } else if (peerNames.length === 1) {
        label = `${peerNames[0]}'s answer`;
      } else {
        label = `${peerNames.join(" and ")}'s answer`;
      }
      builtOptions.push({
        value,
        label,
        holders: holders.map((holder) =>
          holder.kind === "own" ? "own" : holder.id
        ),
      });
    });
    return builtOptions;
  })();
  const DetailsComponent = taskConfig.DetailsComponent;
  const detailsToggle = taskConfig.detailsToggle || {};
  const detailsTitle = taskConfig.detailsTitle || "Case details";
  const tType = treatment.treatmentType ||
    ((treatment.name || "").toLowerCase().includes("no-ai") ? "No-AI Control" :
     (treatment.name || "").toLowerCase().includes("nogroup") ? "nogroup" :
     (treatment.name || "").toLowerCase().includes("control") ? "Control" : undefined);
  const isControlCondition = tType === "Control" || tType === "No-AI Control";
  
  // Filter out the current player
  const otherPlayers = players.filter(p => p.id !== player.id);
  const otherGroupMembers = isBaseRateStaticSyntheticGroup
    ? staticSyntheticPeers
    : otherPlayers;
  const hasHelpfulnessResponse =
    !compensation.hasHelpfulnessRating ||
    otherGroupMembers.length === 0 ||
    Boolean(
      mostHelpfulEarlierParticipantResponse ||
        player.stage.get("mostHelpfulEarlierParticipantResponse")
    );

  // Final confidence field (post-peer conditions only), sourced from taskConfig
  // so it matches the scale and validation of the pre-lobby confidence fields.
  const parsedFinalConfidence = finalConfidence === "" ? null : Number(finalConfidence);
  const parsedFinalConfidenceMin = Number(taskConfig.confidenceMin);
  const parsedFinalConfidenceMax = Number(taskConfig.confidenceMax);
  const finalConfidenceMin = Number.isFinite(parsedFinalConfidenceMin)
    ? parsedFinalConfidenceMin
    : 0;
  const finalConfidenceMax = Number.isFinite(parsedFinalConfidenceMax)
    ? parsedFinalConfidenceMax
    : 100;
  const requireWholeNumberFinalConfidence =
    taskConfig.requireWholeNumberConfidence === true;
  const finalConfidenceLabel =
    taskConfig.confidenceFieldLabel ||
    `How confident are you in this prediction? (${finalConfidenceMin}-${finalConfidenceMax}%)`;
  const finalConfidenceErrorText =
    taskConfig.confidenceErrorText ||
    (requireWholeNumberFinalConfidence
      ? `Please enter a whole-number confidence between ${finalConfidenceMin} and ${finalConfidenceMax}.`
      : `Please enter a confidence between ${finalConfidenceMin} and ${finalConfidenceMax}.`);
  const finalConfidenceInRange =
    parsedFinalConfidence !== null &&
    Number.isFinite(parsedFinalConfidence) &&
    parsedFinalConfidence >= finalConfidenceMin &&
    parsedFinalConfidence <= finalConfidenceMax &&
    (!requireWholeNumberFinalConfidence || Number.isInteger(parsedFinalConfidence));
  const hasFinalConfidence = !isPostPeerReflection || finalConfidenceInRange;

  // The follow-up arms rate the two responses once, at the peer review, as the
  // main study does (postPeerNoRerate), so this screen asks for no second rating.
  const collectsPostRating =
    isPostPeerReflection && !usesPostPeerNoRerate(treatment);
  const postRatingPeerIds = collectsPostRating
    ? otherGroupMembers.map((groupMember) => String(groupMember.id))
    : [];
  const hasRatedAllPeersPost =
    !collectsPostRating ||
    postRatingPeerIds.length === 0 ||
    postRatingPeerIds.every((pid) =>
      Number.isFinite(Number(peerConvincingnessRatingsPost[pid]))
    );

  // Follow-up arms (postPeerChangedAnswerConfidence): a participant whose final
  // estimate differs from the estimate given right after the peer review also
  // reports the confidence still assigned to that earlier estimate, and the two
  // confidences may sum to at most 100. This is the main study's third
  // confidence, asked at the screen that follows the activity. The reference
  // is the estimate the "You previously estimated" line shows: the post-peer
  // estimate, or the initial one if the post-peer stage timed out empty.
  const changedAnswerConfig = taskConfig.postPeerChangedAnswerConfidence || null;
  const collectsPreviousAnswerConfidence =
    isPostPeerReflection &&
    usesPostPeerChangedAnswerConfidence(treatment) &&
    Boolean(changedAnswerConfig);
  const previousAnswerReference = (() => {
    if (!collectsPreviousAnswerConfidence) return "";
    const postPeer = normalizePredictionChoice(player.get("postPeerPrediction"));
    if (postPeer !== "") return postPeer;
    return normalizePredictionChoice(
      (game.get("playerBeliefs") || {})[player.id]?.initialBelief
    );
  })();
  const requiresPreviousAnswerConfidence =
    collectsPreviousAnswerConfidence &&
    hasVoted &&
    previousAnswerReference !== "" &&
    recordedFinalBelief !== previousAnswerReference;
  const parsedPreviousAnswerConfidence =
    previousAnswerConfidence === "" ? null : Number(previousAnswerConfidence);
  const previousAnswerConfidenceError = (() => {
    if (!requiresPreviousAnswerConfidence) return "";
    const inRange =
      parsedPreviousAnswerConfidence !== null &&
      Number.isFinite(parsedPreviousAnswerConfidence) &&
      parsedPreviousAnswerConfidence >= finalConfidenceMin &&
      parsedPreviousAnswerConfidence <= finalConfidenceMax &&
      (!requireWholeNumberFinalConfidence ||
        Number.isInteger(parsedPreviousAnswerConfidence));
    if (!inRange) return changedAnswerConfig.errorText;
    const combinedMax = Number(changedAnswerConfig.combinedMax);
    if (
      finalConfidenceInRange &&
      Number.isFinite(combinedMax) &&
      parsedFinalConfidence + parsedPreviousAnswerConfidence > combinedMax
    ) {
      return changedAnswerConfig.errorText;
    }
    return "";
  })();
  const hasPreviousAnswerConfidence =
    !requiresPreviousAnswerConfidence || previousAnswerConfidenceError === "";

  function onRatePeerPost(peerId, score) {
    const pid = String(peerId);
    const numericScore = Number(score);
    if (!Number.isFinite(numericScore)) return;
    const timestamp = Date.now();
    const nextRatings = { ...peerConvincingnessRatingsPost, [pid]: numericScore };
    setPeerConvincingnessRatingsPost(nextRatings);
    player.stage.set("peerConvincingnessRatingsPost", nextRatings);
    player.stage.set("peerConvincingnessRatingsPostAt", timestamp);
    try {
      player.set("peerConvincingnessRatingsPost", nextRatings);
      player.set("peerConvincingnessRatingsPostAt", timestamp);
    } catch (_) {}
    try {
      savePlayerMeta({
        player,
        name: "peerConvincingnessRatingsPost",
        value: { ratings: nextRatings, updatedAt: timestamp },
        debugTag: "final vote: post-reflection peer convincingness rating",
      });
    } catch (_) {}
    try {
      const events = round.get("eventLog") || [];
      events.push({
        t: timestamp,
        type: "rating",
        stageId: stage?.id,
        stageName: stage?.get("name"),
        playerId: player.id,
        action: "peer_convincingness_rating_post",
        peerId: pid,
        score: numericScore,
      });
      round.set("eventLog", events);
    } catch (_) {}
  }

  function onFinalConfidenceChange(value) {
    setFinalConfidence(value);
    const parsed = value === "" ? null : Number(value);
    const inRange =
      parsed !== null &&
      Number.isFinite(parsed) &&
      parsed >= finalConfidenceMin &&
      parsed <= finalConfidenceMax &&
      (!requireWholeNumberFinalConfidence || Number.isInteger(parsed));
    if (!inRange) return;
    const timestamp = Date.now();
    player.set("finalConfidence", parsed);
    player.set("finalConfidenceAt", timestamp);
    player.stage.set("finalConfidence", parsed);
    player.stage.set("finalConfidenceAt", timestamp);
    try {
      savePlayerMeta({
        player,
        name: "finalConfidence",
        value: { confidence: parsed, timestamp },
        debugTag: "final vote: final confidence",
      });
    } catch (_) {}
  }
  function onVoteMostConvincing(selectedPlayerId) {
    const timestamp = Date.now();
    setMostConvincingPlayerId(selectedPlayerId);
    
    // Store the vote
    player.stage.set("mostConvincingPlayerId", selectedPlayerId);
    player.stage.set("mostConvincingPlayerVoteAt", timestamp);
    savePlayerMeta({ 
      player, 
      name: "mostConvincingPlayerVote", 
      value: { selectedPlayerId, timestamp }, 
      debugTag: "final vote: most convincing player" 
    });
    
    try {
      const events = round.get("eventLog") || [];
      events.push({ 
        t: timestamp, 
        type: "click", 
        stageId: stage?.id, 
        stageName: stage?.get("name"), 
        playerId: player.id, 
        action: "mostConvincingPlayerVote", 
        value: selectedPlayerId 
      });
      round.set("eventLog", events);
    } catch (_) {}
  }

  function onSelectHelpfulEarlierResponse(selectedResponseId) {
    const timestamp = Date.now();
    setMostHelpfulEarlierParticipantResponse(selectedResponseId);

    player.stage.set("mostHelpfulEarlierParticipantResponse", selectedResponseId);
    player.stage.set("mostHelpfulEarlierParticipantResponseAt", timestamp);
    player.set("mostHelpfulEarlierParticipantResponse", selectedResponseId);
    player.set("mostHelpfulEarlierParticipantResponseAt", timestamp);
    savePlayerMeta({
      player,
      name: "mostHelpfulEarlierParticipantResponse",
      value: { selectedResponseId, timestamp },
      debugTag: "final vote: helpful earlier participant response",
    });

    try {
      const events = round.get("eventLog") || [];
      events.push({
        t: timestamp,
        type: "click",
        stageId: stage?.id,
        stageName: stage?.get("name"),
        playerId: player.id,
        action: "mostHelpfulEarlierParticipantResponse",
        value: selectedResponseId,
      });
      round.set("eventLog", events);
    } catch (_) {}
  }

  function onVote(choice) {
    const timestamp = Date.now();
    const normalizedChoice = normalizePredictionChoice(choice);
    if (!normalizedChoice) {
      return;
    }

    // Store directly on round (for backward compatibility and Result component)
    player.round.set("finalBelief", normalizedChoice);
    player.round.set("finalBeliefAt", timestamp);
    savePlayerMeta({ player, name: "finalBelief", value: { choice: normalizedChoice, timestamp }, debugTag: "final vote: all case" });

    // Also store in playerBeliefs object at game level (for consistency)
    const existingBeliefs = game.get("playerBeliefs") || {};
    if (existingBeliefs[player.id]) {
      existingBeliefs[player.id].finalBelief = normalizedChoice;
      existingBeliefs[player.id].finalBeliefAt = timestamp;
    } else {
      existingBeliefs[player.id] = {
        finalBelief: normalizedChoice,
        finalBeliefAt: timestamp
      };
    }
    game.set("playerBeliefs", existingBeliefs);

    try {
      const events = round.get("eventLog") || [];
      events.push({ t: timestamp, type: "click", stageId: stage?.id, stageName: stage?.get("name"), playerId: player.id, action: "finalBelief", value: normalizedChoice });
      round.set("eventLog", events);
    } catch (_) {}
  }

  function onRecordNumericVote() {
    const normalizedChoice = normalizePredictionChoice(predictionDraft);
    if (!normalizedChoice) {
      setPredictionError(
        predictionInput.errorText ||
          "Please enter a valid final estimate before continuing."
      );
      return;
    }
    setPredictionError("");
    setPredictionDraft(normalizedChoice);
    onVote(normalizedChoice);
  }

  // askall: clicking a choice records the vote immediately through the same
  // onVote path as the numeric input, and keeps the draft in sync so the
  // Continue gate (draftMatchesRecorded) behaves as in the numeric flow. The
  // presented option set and the holders of the chosen value are stored for
  // the analysis alongside the vote itself.
  function onSelectAskAllOption(option) {
    setPredictionDraft(option.value);
    setPredictionError("");
    const timestamp = Date.now();
    try {
      player.set("finalVoteAskAllChoice", {
        value: option.value,
        holders: option.holders,
        optionSet: askAllOptions.map(({ value, holders }) => ({
          value,
          holders,
        })),
        at: timestamp,
      });
    } catch (_) {}
    onVote(option.value);
  }

  function onContinue() {
    const timestamp = Date.now();
    if (compensation.hasHelpfulnessRating && !hasHelpfulnessResponse) {
      return;
    }
    if (!hasRatedAllPeersPost || !hasFinalConfidence || !hasPreviousAnswerConfidence) {
      return;
    }
    // Written once, here, from the recorded final estimate, so a value typed
    // before the participant moved back to the previous estimate is not kept.
    // Null when the estimate did not change, as in the main study's field.
    if (collectsPreviousAnswerConfidence) {
      const storedPreviousAnswerConfidence = requiresPreviousAnswerConfidence
        ? parsedPreviousAnswerConfidence
        : null;
      player.set("finalConfidenceForPreviousAnswer", storedPreviousAnswerConfidence);
      player.set("finalConfidenceForPreviousAnswerAt", timestamp);
      player.set("finalConfidencePreviousAnswerReference", previousAnswerReference);
      try {
        savePlayerMeta({
          player,
          name: "finalConfidenceForPreviousAnswer",
          value: {
            confidence: storedPreviousAnswerConfidence,
            previousAnswer: previousAnswerReference,
            finalAnswer: recordedFinalBelief,
            timestamp,
          },
          debugTag: "final vote: confidence still assigned to the previous estimate",
        });
      } catch (_) {}
    }
    // Submit the stage to advance. The game ends on this submit and the
    // server's end-of-game export reads the behavior log, so flush it first.
    flushBehaviorTracking();
    player.stage.set("submit", true);
    player.stage.set("submittedAt", timestamp);
    if (isBaseRateStaticSyntheticGroup) {
      player.set("readyToExit", true);
    }
    try {
      const events = round.get("eventLog") || [];
      events.push({ t: timestamp, type: "click", stageId: stage?.id, stageName: stage?.get("name"), playerId: player.id, action: "continue_finalVote" });
      round.set("eventLog", events);
    } catch (_) {}
  }

  // After submitting, show waiting ONLY when actually in finalVote stage
  if (stage?.get("name") === "finalVote" && player?.stage.get("submit")) {
    if (isBaseRateStaticSyntheticGroup || players.length === 1) {
      return (
        <div className="max-w-3xl mx-auto p-6 text-center text-gray-600">
          Loading exit survey...
        </div>
      );
    }
    return <WaitingForOthers stageName="finalVote" />;
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* The syngroup conditions have no group and nothing is voted on, so they
          use the same noun as the rest of their copy. */}
      <h2 className="text-2xl font-bold mb-6">
        {isBaseRateStaticSyntheticGroup ? "Final Estimate" : "Final Vote"}
      </h2>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <p className="text-lg mb-4">
          {taskConfig.finalVotePrompt}
        </p>

        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">{detailsTitle}</h3>
          <button
            type="button"
            className="text-sm px-3 py-2 rounded-md font-semibold shadow-sm border border-empirica-500 text-white bg-empirica-600 hover:bg-empirica-700 focus:outline-none focus:ring-2 focus:ring-empirica-300"
            onClick={() => setShowLoanCase((prev) => !prev)}
          >
            {showLoanCase ? (detailsToggle.hide || "🙈 Hide case") : (detailsToggle.show || "👁️ Show case")}
          </button>
        </div>
        {showLoanCase ? (
          DetailsComponent ? <DetailsComponent /> : null
        ) : (
          <div className="text-sm text-gray-600 border border-dashed border-gray-300 rounded-md p-3 bg-gray-50">
            {detailsToggle.hiddenText || "Case hidden. Click \"Show case\" to view the details again."}
          </div>
        )}

        {(() => {
          // Initial beliefs are stored in playerBeliefs object on game.
          // In the post-peer conditions the participant gave a second estimate on
          // the postPeerBelief stage, so show that one: it is the most recent
          // estimate they made and the one the final vote is revising.
          const playerBeliefs = game.get("playerBeliefs") || {};
          const initialBelief = playerBeliefs[player.id]?.initialBelief;
          const postPeerPrediction = isPostPeerReflection
            ? player.get("postPeerPrediction")
            : undefined;
          const isFilled = (value) =>
            value !== undefined && value !== null && String(value).trim() !== "";
          const priorBelief = isFilled(postPeerPrediction)
            ? postPeerPrediction
            : initialBelief;
          return isFilled(priorBelief) ? (
            <div className="mb-6">
              <p className="text-lg">
                You previously estimated: <strong>{formatBelief(priorBelief)}</strong>
              </p>
            </div>
          ) : null;
        })()}
      </div>

      {isBaseRateStaticSyntheticGroup && otherGroupMembers.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                Other Participants' Responses
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Reopen the two other participants' responses while making your final estimate.
              </p>
            </div>
            <button
              type="button"
              className="shrink-0 text-sm px-3 py-2 rounded-md font-semibold shadow-sm border border-empirica-500 text-white bg-empirica-600 hover:bg-empirica-700 focus:outline-none focus:ring-2 focus:ring-empirica-300"
              onClick={() => setShowPeerRationales((prev) => !prev)}
            >
              {showPeerRationales ? "Hide responses" : "Show responses"}
            </button>
          </div>

          {showPeerRationales && (
            <div className="mt-5 space-y-4">
              {otherGroupMembers.map((groupMember) => {
                const memberId = groupMember.id;
                const nickname = groupMember?.name || getNicknameFromId(memberId);
                const animal = groupMember?.assignedAnimal || getAnimalFromId(memberId);
                const emoji = getAnimalEmoji(animal);
                const rationaleText =
                  String(groupMember?.rationaleText || "").trim() ||
                  "This earlier participant response is unavailable.";
                const estimate = groupMember?.initialBelief;
                const hasEstimate =
                  estimate !== undefined &&
                  estimate !== null &&
                  String(estimate).trim() !== "";

                return (
                  <div
                    key={memberId}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                  >
                    <div className="mb-3 flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-white shadow flex items-center justify-center text-2xl">
                        <span aria-hidden>{emoji}</span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{nickname}</div>
                        {hasEstimate && (
                          <div className="text-sm text-gray-600">
                            Estimate:{" "}
                            <span className="font-medium">
                              {formatBelief(estimate)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="whitespace-pre-wrap text-sm leading-6 text-gray-800">
                      {rationaleText}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Second convincingness rating (post-peer conditions only), rendered
          above the final prediction input. */}
      {collectsPostRating && otherGroupMembers.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold mb-3">
            How convincing do you find each response now?
          </h3>
          <p className="text-base text-gray-600 mb-4">
            Please rate each of the two other participants' responses before giving
            your final estimate.
          </p>

          <div className="space-y-4">
            {otherGroupMembers.map((groupMember) => {
              const memberId = groupMember.id;
              const peerKey = String(memberId);
              const nickname = groupMember?.name || getNicknameFromId(memberId);
              const animal =
                groupMember?.assignedAnimal || getAnimalFromId(memberId);
              const emoji = getAnimalEmoji(animal);
              const currentScore = peerConvincingnessRatingsPost[peerKey];

              return (
                <div
                  key={memberId}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                >
                  <div className="mb-3 flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-white shadow flex items-center justify-center text-2xl">
                      <span aria-hidden>{emoji}</span>
                    </div>
                    <div className="font-semibold text-gray-900">{nickname}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[10px] text-gray-600 whitespace-nowrap">
                      Not at all
                    </span>
                    {CONVINCINGNESS_SCALE.map((option) => {
                      const selected = Number(currentScore) === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          aria-label={`${option.label} convincing`}
                          aria-pressed={selected}
                          className={`h-8 w-8 rounded-full border text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-emerald-300 ${
                            selected
                              ? "bg-emerald-600 border-emerald-700 text-white"
                              : "bg-white border-emerald-300 text-emerald-900 hover:bg-emerald-100"
                          }`}
                          onClick={() => onRatePeerPost(memberId, option.value)}
                        >
                          {option.value}
                        </button>
                      );
                    })}
                    <span className="text-[10px] text-gray-600 whitespace-nowrap">
                      Extremely
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {!hasRatedAllPeersPost && (
            <p className="text-sm text-gray-500 mt-4">
              Please rate both responses before continuing.
            </p>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        {/* Numeric tasks call this an estimate everywhere else on the page
            ("Record estimate", "You previously estimated", the prompt above), so
            the heading follows the response space rather than the stage name. */}
        <h3 className="text-xl font-bold mb-3">
          {isNumericPrediction
            ? "What is your final estimate?"
            : "What is your final prediction?"}
        </h3>

        {postPeerClosedChoice && isNumericPrediction && askAllOptions.length > 0 ? (
          <div className="max-w-md mx-auto">
            <p className="text-sm text-gray-700 mb-3">
              As announced at the start, your final answer must be one of the
              choices below: keep your own estimate, or adopt one of the two
              other participants' answers.
            </p>
            <div className="space-y-3">
              {askAllOptions.map((option) => {
                const isSelected = recordedFinalBelief === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onSelectAskAllOption(option)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected
                        ? "bg-blue-100 border-blue-500 shadow-md"
                        : "bg-gray-50 border-gray-300 hover:border-blue-300 hover:bg-blue-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-gray-900">
                        {option.label}
                      </span>
                      <span className="text-lg font-bold text-gray-900">
                        {formatBelief(option.value)}
                      </span>
                    </div>
                    {isSelected && (
                      <div className="text-sm text-blue-600 font-medium mt-1">
                        ✓ Selected
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ) : isNumericPrediction ? (
          <div className="max-w-md mx-auto">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {predictionInput.label || "Enter your final estimate"}
            </label>
            <div className="flex gap-3 items-start mb-3">
              <input
                type="number"
                min={predictionInput.min}
                max={predictionInput.max}
                step={predictionInput.step || 1}
                inputMode="numeric"
                className={`border rounded px-3 py-2 flex-1 ${NO_SPINNER_CLASS}`}
                placeholder={predictionInput.placeholder || ""}
                value={predictionDraft}
                onChange={(e) => {
                  setPredictionDraft(e.target.value);
                  if (predictionError) setPredictionError("");
                }}
                {...typedNumberInputProps({
                  onKeyDown: (e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onRecordNumericVote();
                    }
                  },
                })}
              />
              <Button
                className={`px-5 py-2 ${normalizedDraft ? "bg-blue-500 hover:bg-blue-600" : "bg-gray-300 cursor-not-allowed"}`}
                handleClick={onRecordNumericVote}
                disabled={!normalizedDraft}
              >
                Record estimate
              </Button>
            </div>
            {predictionInput.helpText && (
              <p className="text-sm text-gray-500 mb-2">{predictionInput.helpText}</p>
            )}
            {predictionError && (
              <p className="text-sm text-red-600 mb-2">{predictionError}</p>
            )}
            {hasVoted && !draftMatchesRecorded && (
              <p className="text-sm text-amber-700 mb-2">
                Your recorded estimate is still <strong>{formatBelief(recordedFinalBelief)}</strong>. Click
                {" "}Record estimate to update it.
              </p>
            )}
          </div>
        ) : (
          <div className="flex justify-center gap-4 mb-4">
            {options.map((option) => {
              const label = option.buttonLabel || option.label || option.value;
              const isSelected = recordedFinalBelief === option.value;
              return (
                <Button
                  key={option.value}
                  className={`px-8 py-4 ${isSelected ? "bg-green-600 hover:bg-green-700" : "bg-blue-500 hover:bg-blue-600"}`}
                  handleClick={() => onVote(option.value)}
                >
                  {label}
                </Button>
              );
            })}
          </div>
        )}
        
        {hasVoted && (
          <div className="text-center mt-4 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-green-800 font-medium">
                {(() => {
                  const selectedOption = options.find((option) => option.value === recordedFinalBelief);
                  const selectedLabel = selectedOption?.label || formatBelief(recordedFinalBelief);
                  return (
                    <>
                      ✓ You chose: <strong className="text-green-900">{selectedLabel}</strong>
                    </>
                  );
                })()}
              </p>
            </div>
          </div>
        )}

        {/* Final confidence (post-peer conditions only), same scale and
            validation as the earlier belief elicitations. */}
        {isPostPeerReflection && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <label className="block text-lg font-semibold mb-2">
              {finalConfidenceLabel}
            </label>
            <input
              className={`w-full border border-gray-300 rounded-md p-3 ${NO_SPINNER_CLASS}`}
              type="number"
              data-integrity-field="finalvote-confidence"
              min={finalConfidenceMin}
              max={finalConfidenceMax}
              step={requireWholeNumberFinalConfidence ? "1" : "any"}
              placeholder={
                taskConfig.confidencePlaceholder ||
                `${finalConfidenceMin}-${finalConfidenceMax}`
              }
              value={finalConfidence}
              onChange={(e) => onFinalConfidenceChange(e.target.value)}
              {...typedNumberInputProps()}
            />
            {taskConfig.confidenceHelpText && (
              <p className="text-sm text-gray-600 mt-2">
                {taskConfig.confidenceHelpText}
              </p>
            )}
            {!finalConfidenceInRange && (
              <p className="text-sm text-gray-500 mt-2">
                {finalConfidenceErrorText}
              </p>
            )}
          </div>
        )}

        {/* Confidence still assigned to the previous estimate (follow-up arms
            only), shown once the recorded final estimate differs from it. */}
        {requiresPreviousAnswerConfidence && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <label className="block text-lg font-semibold mb-2">
              {changedAnswerConfig.labelTemplate(formatBelief(previousAnswerReference))}
            </label>
            <input
              className={`w-full border border-gray-300 rounded-md p-3 ${NO_SPINNER_CLASS}`}
              type="number"
              data-integrity-field="finalvote-previous-answer-confidence"
              min={finalConfidenceMin}
              max={finalConfidenceMax}
              step={requireWholeNumberFinalConfidence ? "1" : "any"}
              placeholder={changedAnswerConfig.placeholder || "0-100"}
              value={previousAnswerConfidence}
              onChange={(e) => setPreviousAnswerConfidence(e.target.value)}
              {...typedNumberInputProps()}
            />
            {changedAnswerConfig.helpText && (
              <p className="text-sm text-gray-600 mt-2">
                {changedAnswerConfig.helpText}
              </p>
            )}
            {previousAnswerConfidenceError && (
              <p className="text-sm text-gray-500 mt-2">
                {previousAnswerConfidenceError}
              </p>
            )}
          </div>
        )}
      </div>

      {compensation.hasHelpfulnessRating &&
        isBaseRateStaticSyntheticGroup &&
        otherGroupMembers.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold mb-3">Which other participant response was most helpful?</h3>
          <p className="text-base text-gray-600 mb-4">
            This is for research purposes only and does not affect your bonus.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {otherGroupMembers.map((groupMember) => {
              const memberId = groupMember.id;
              const nickname = groupMember?.name || getNicknameFromId(memberId);
              const animal = groupMember?.assignedAnimal || getAnimalFromId(memberId);
              const emoji = getAnimalEmoji(animal);
              const isSelected =
                mostHelpfulEarlierParticipantResponse === memberId ||
                player.stage.get("mostHelpfulEarlierParticipantResponse") === memberId;

              return (
                <button
                  key={memberId}
                  onClick={() => onSelectHelpfulEarlierResponse(memberId)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? "bg-blue-100 border-blue-500 shadow-md"
                      : "bg-gray-50 border-gray-300 hover:border-blue-300 hover:bg-blue-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-16 w-16 rounded-full bg-white shadow-lg flex items-center justify-center text-4xl flex-shrink-0">
                      <span aria-hidden>{emoji}</span>
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">{nickname}</div>
                      {isSelected && (
                        <div className="text-sm text-blue-600 font-medium mt-1">✓ Selected</div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}

            <button
              onClick={() => onSelectHelpfulEarlierResponse("neither")}
              className={`p-4 rounded-lg border-2 transition-all ${
                mostHelpfulEarlierParticipantResponse === "neither" ||
                player.stage.get("mostHelpfulEarlierParticipantResponse") === "neither"
                  ? "bg-blue-100 border-blue-500 shadow-md"
                  : "bg-gray-50 border-gray-300 hover:border-blue-300 hover:bg-blue-50"
              }`}
            >
              <div className="font-semibold text-gray-900">Neither is helpful</div>
              {(mostHelpfulEarlierParticipantResponse === "neither" ||
                player.stage.get("mostHelpfulEarlierParticipantResponse") === "neither") && (
                <div className="text-sm text-blue-600 font-medium mt-1">✓ Selected</div>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Most convincing player question - only for live Control and No-AI Control conditions */}
      {!isBaseRateStaticSyntheticGroup && isControlCondition && otherGroupMembers.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-xl font-bold mb-3">Who provided the most convincing reasoning?</h3>
          <p className="text-base text-gray-600 mb-4">
            The person who receives the most votes will receive a {compensation.mostConvincingBonusText} bonus.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            {otherGroupMembers.map((groupMember) => {
              const isSynthetic = !groupMember?.get;
              const memberId = isSynthetic ? groupMember.id : groupMember.id;
              const nickname = isSynthetic
                ? groupMember?.name || getNicknameFromId(memberId)
                : getNicknameFromPlayer(groupMember);
              const animal = isSynthetic
                ? groupMember?.assignedAnimal || getAnimalFromId(memberId)
                : getAnimalFromPlayer(groupMember);
              const emoji = getAnimalEmoji(animal);
              const isSelected =
                mostConvincingPlayerId === memberId ||
                player.stage.get("mostConvincingPlayerId") === memberId;
              
              return (
                <button
                  key={memberId}
                  onClick={() => onVoteMostConvincing(memberId)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? "bg-blue-100 border-blue-500 shadow-md"
                      : "bg-gray-50 border-gray-300 hover:border-blue-300 hover:bg-blue-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-16 w-16 rounded-full bg-white shadow-lg flex items-center justify-center text-4xl flex-shrink-0">
                      <span aria-hidden>{emoji}</span>
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">{nickname}</div>
                      {isSelected && (
                        <div className="text-sm text-blue-600 font-medium mt-1">✓ Selected</div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Continue button - always visible but disabled until vote is made */}
      <div className="flex justify-center mt-8">
        <Button
          className={`px-8 py-4 text-lg font-semibold ${hasVoted && draftMatchesRecorded && hasHelpfulnessResponse && hasRatedAllPeersPost && hasFinalConfidence && hasPreviousAnswerConfidence ? "bg-green-500 hover:bg-green-600" : "bg-gray-300 cursor-not-allowed"}`}
          handleClick={onContinue}
          disabled={
            !hasVoted ||
            !draftMatchesRecorded ||
            !hasHelpfulnessResponse ||
            !hasRatedAllPeersPost ||
            !hasFinalConfidence ||
            !hasPreviousAnswerConfidence
          }
        >
          Continue
        </Button>
      </div>

      {/* Typing a number is not enough: the estimate is only saved once
          "Record estimate" is clicked, so say so while Continue is blocked.
          The askall conditions have no numeric input, so they get their own
          hint instead. */}
      {isPostPeerReflection &&
        isNumericPrediction &&
        !postPeerClosedChoice &&
        (!hasVoted || !draftMatchesRecorded) && (
        <p className="mt-3 text-center text-sm text-gray-600">
          Please click <strong>Record estimate</strong> above to save your final estimate before continuing.
        </p>
      )}
      {postPeerClosedChoice && !hasVoted && (
        <p className="mt-3 text-center text-sm text-gray-600">
          Please select one of the answer choices above before continuing.
        </p>
      )}
    </div>
  );
} 
